// src/pages/Operator/ParcelCreate.jsx
import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import axios from "../../api/axios";

// ========== FONCTIONS UTILITAIRES ==========

const formatToTwoDecimals = (value) => {
  if (!value && value !== 0) return null;
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return num.toFixed(2);
};

const validateNumericField = (value, fieldName) => {
  if (!value && value !== 0) return null;
  const num = parseFloat(value);
  if (isNaN(num)) {
    toast.error(`${fieldName} doit être un nombre valide`);
    return null;
  }
  if (num <= 0) {
    toast.error(`${fieldName} doit être supérieur à 0`);
    return null;
  }
  if (num > 99999999) {
    toast.error(`${fieldName} est trop grand (max 99,999,999)`);
    return null;
  }
  return num;
};

const validateRateField = (value, fieldName) => {
  if (!value && value !== 0) return null;
  const num = parseFloat(value);
  if (isNaN(num)) {
    toast.error(`${fieldName} doit être un nombre valide`);
    return null;
  }
  if (num <= 0) {
    toast.error(`${fieldName} doit être supérieur à 0`);
    return null;
  }
  if (num > 999999999999) {
    toast.error(`${fieldName} est trop grand (max 999,999,999,999)`);
    return null;
  }
  return Math.round(num);
};

// ========== COMPOSANT PRINCIPAL ==========

const ParcelCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [estimatedCost, setEstimatedCost] = useState(0);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);

  const DEFAULT_SENDER_NAME = "CAC";
  const DEFAULT_SENDER_ADDRESS = "佛山市南海区里水镇湖州村湖州大道10号";

  const [formData, setFormData] = useState({
    sender_name: DEFAULT_SENDER_NAME,
    sender_address: DEFAULT_SENDER_ADDRESS,
    sender_phone: "",
    recipient_name: "",
    recipient_address: "",
    recipient_phone: "",
    recipient_email: "",
    description: "",
    shipment_type: "sea",
    weight_kg: "",
    rate_per_kg: "",
    volume_cbm: "",
    rate_per_cbm: "",
    expected_delivery_date: "",
    // ✅ CHAMPS MARITIMES
    container_number: "",
    current_vessel_name: "",
    shipping_line_name: "",
    loading_port: "",
    discharging_port: "",
    bill_of_lading: "",
  });

  // ========== CHARGEMENT WHATSAPP ==========

  useEffect(() => {
    fetchWhatsappNumbers();
  }, []);

  const fetchWhatsappNumbers = async () => {
    try {
      const response = await axios.get("/whatsapp/public/");
      let numbers = [];
      if (response.data && response.data.results) {
        numbers = response.data.results;
      } else if (Array.isArray(response.data)) {
        numbers = response.data;
      }

      if (numbers.length > 0) {
        setWhatsappNumbers(numbers);
        const defaultNumber = numbers.find((n) => n.is_default) || numbers[0];
        if (defaultNumber) {
          setFormData((prev) => ({
            ...prev,
            sender_phone: defaultNumber.phone_number,
          }));
        }
      }
    } catch (err) {
      console.error("Erreur chargement numéros WhatsApp:", err);
      setFormData((prev) => ({
        ...prev,
        sender_phone: "+237 612345678",
      }));
    } finally {
      setLoadingWhatsapp(false);
    }
  };

  // ========== GESTION DES COÛTS ==========

  const updateEstimatedCost = (data) => {
    if (data.shipment_type === "air" && data.weight_kg && data.rate_per_kg) {
      setEstimatedCost(
        parseFloat(data.weight_kg) * parseFloat(data.rate_per_kg),
      );
    } else if (
      data.shipment_type === "sea" &&
      data.volume_cbm &&
      data.rate_per_cbm
    ) {
      setEstimatedCost(
        parseFloat(data.volume_cbm) * parseFloat(data.rate_per_cbm),
      );
    } else {
      setEstimatedCost(0);
    }
  };

  // ========== GESTION DES CHAMPS ==========

  const handleChange = (e) => {
    const newData = { ...formData, [e.target.name]: e.target.value };
    setFormData(newData);
    updateEstimatedCost(newData);
  };

  const handleShipmentTypeChange = (e) => {
    setFormData({
      ...formData,
      shipment_type: e.target.value,
      weight_kg: "",
      rate_per_kg: "",
      volume_cbm: "",
      rate_per_cbm: "",
    });
    setEstimatedCost(0);
  };

  // ========== SOUMISSION ==========

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.recipient_phone) {
        toast.error("Le téléphone du destinataire est obligatoire");
        setLoading(false);
        return;
      }

      let submitData = {
        sender_name: formData.sender_name || DEFAULT_SENDER_NAME,
        sender_address: formData.sender_address || DEFAULT_SENDER_ADDRESS,
        sender_phone: formData.sender_phone,
        recipient_name: formData.recipient_name,
        recipient_address: formData.recipient_address,
        recipient_phone: formData.recipient_phone,
        recipient_email: formData.recipient_email || "",
        description: formData.description,
        shipment_type: formData.shipment_type,
        expected_delivery_date: formData.expected_delivery_date || null,
        // ✅ CHAMPS MARITIMES
        container_number: formData.container_number || "",
        current_vessel_name: formData.current_vessel_name || "",
        shipping_line_name: formData.shipping_line_name || "",
        loading_port: formData.loading_port || "",
        discharging_port: formData.discharging_port || "",
        bill_of_lading: formData.bill_of_lading || "",
      };

      if (formData.shipment_type === "air") {
        const weight = validateNumericField(formData.weight_kg, "Le poids");
        if (weight === null && formData.weight_kg !== "") {
          setLoading(false);
          return;
        }
        submitData.weight_kg =
          weight !== null ? formatToTwoDecimals(weight) : null;

        const rate = validateRateField(formData.rate_per_kg, "Le tarif/kg");
        if (rate === null && formData.rate_per_kg !== "") {
          setLoading(false);
          return;
        }
        submitData.rate_per_kg = rate;

        delete submitData.volume_cbm;
        delete submitData.rate_per_cbm;
      } else {
        const volume = validateNumericField(
          formData.volume_cbm,
          "Le volume (CBM)",
        );
        if (volume === null && formData.volume_cbm !== "") {
          setLoading(false);
          return;
        }
        submitData.volume_cbm =
          volume !== null ? formatToTwoDecimals(volume) : null;

        const rate = validateRateField(formData.rate_per_cbm, "Le tarif/CBM");
        if (rate === null && formData.rate_per_cbm !== "") {
          setLoading(false);
          return;
        }
        submitData.rate_per_cbm = rate;

        delete submitData.weight_kg;
        delete submitData.rate_per_kg;
      }


      const response = await axios.post("/parcels/", submitData);
      toast.success(`✅ Colis ${response.data.tracking_number} créé !`);

      setTimeout(() => {
        navigate("/operator/");
      }, 1500);
    } catch (err) {
      console.error("❌ Erreur création:", err.response?.data);

      if (err.response?.data) {
        const errors = err.response.data;
        if (typeof errors === "object") {
          Object.keys(errors).forEach((key) => {
            const errorMsg = Array.isArray(errors[key])
              ? errors[key][0]
              : errors[key];
            toast.error(`${key}: ${errorMsg}`);
          });
        } else {
          toast.error(errors);
        }
      } else {
        toast.error("Erreur lors de la création du colis");
      }
    } finally {
      setLoading(false);
    }
  };

  // ========== RENDU ==========

  return (
    <Container className="py-4">
      <Card className="border-0 shadow-lg rounded-4">
        <Card.Header className="bg-white border-0 pt-4">
          <h2 className="fw-bold" style={{ color: "#C62828" }}>
            📦 Créer un colis
          </h2>
          <p className="text-muted mb-0">
            Remplissez les informations ci-dessous pour créer un nouveau colis
          </p>
        </Card.Header>
        <Card.Body className="p-4">
          <Form onSubmit={handleSubmit}>
            {/* ========== EXPÉDITEUR ========== */}
            <h4 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              📤 Expéditeur
            </h4>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Label>Nom *</Form.Label>
                <Form.Control
                  type="text"
                  name="sender_name"
                  value={formData.sender_name}
                  onChange={handleChange}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>Téléphone *</Form.Label>
                <Form.Control
                  type="text"
                  name="sender_phone"
                  value={formData.sender_phone}
                  onChange={handleChange}
                  required
                  disabled={loadingWhatsapp}
                />
                {loadingWhatsapp && (
                  <Form.Text className="text-muted">
                    <Spinner animation="border" size="sm" className="me-1" />
                    Chargement du numéro...
                  </Form.Text>
                )}
              </Col>
              <Col md={12}>
                <Form.Label>Adresse *</Form.Label>
                <Form.Control
                  type="text"
                  name="sender_address"
                  value={formData.sender_address}
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>

            {/* ========== DESTINATAIRE ========== */}
            <h4 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              📥 Destinataire
            </h4>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Label>Nom *</Form.Label>
                <Form.Control
                  type="text"
                  name="recipient_name"
                  value={formData.recipient_name}
                  onChange={handleChange}
                  required
                />
              </Col>
              <Col md={6}>
                <Form.Label>Téléphone *</Form.Label>
                <Form.Control
                  type="text"
                  name="recipient_phone"
                  value={formData.recipient_phone}
                  onChange={handleChange}
                  required
                  placeholder="+223 78 02 33 55"
                />
              </Col>
              <Col md={12}>
                <Form.Label>Email (optionnel)</Form.Label>
                <Form.Control
                  type="email"
                  name="recipient_email"
                  value={formData.recipient_email}
                  onChange={handleChange}
                  placeholder="client@email.com"
                />
                <Form.Text className="text-muted">
                  L'email est optionnel. Les notifications seront envoyées par
                  WhatsApp.
                </Form.Text>
              </Col>
              <Col md={12}>
                <Form.Label>Adresse *</Form.Label>
                <Form.Control
                  type="text"
                  name="recipient_address"
                  value={formData.recipient_address}
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>

            {/* ========== DESCRIPTION ========== */}
            <h4 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              📦 Description
            </h4>
            <Row className="mb-4">
              <Col md={12}>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  placeholder="Décrivez le contenu du colis..."
                />
              </Col>
            </Row>

            {/* ========== TRANSPORT ========== */}
            <h4 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              🚚 Transport
            </h4>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Label>Type *</Form.Label>
                <Form.Select
                  name="shipment_type"
                  value={formData.shipment_type}
                  onChange={handleShipmentTypeChange}
                >
                  <option value="air">✈️ Aérien</option>
                  <option value="sea">🚢 Maritime</option>
                </Form.Select>
              </Col>

              {formData.shipment_type === "air" ? (
                <>
                  <Col md={3}>
                    <Form.Label>Poids (kg) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="weight_kg"
                      value={formData.weight_kg}
                      onChange={handleChange}
                      required
                      placeholder="0.00"
                    />
                    <Form.Text className="text-muted">
                      Max 99,999,999.99 kg (2 décimales)
                    </Form.Text>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Tarif/kg (FCFA) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      name="rate_per_kg"
                      value={formData.rate_per_kg}
                      onChange={handleChange}
                      required
                      placeholder="0"
                    />
                    <Form.Text className="text-muted">
                      Montant en FCFA (entier)
                    </Form.Text>
                  </Col>
                </>
              ) : (
                <>
                  <Col md={3}>
                    <Form.Label>Volume (CBM) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="volume_cbm"
                      value={formData.volume_cbm}
                      onChange={handleChange}
                      required
                      placeholder="0.00"
                    />
                    <Form.Text className="text-muted">
                      Max 99,999,999.99 CBM (2 décimales)
                    </Form.Text>
                  </Col>
                  <Col md={3}>
                    <Form.Label>Tarif/CBM (FCFA) *</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      name="rate_per_cbm"
                      value={formData.rate_per_cbm}
                      onChange={handleChange}
                      required
                      placeholder="0"
                    />
                    <Form.Text className="text-muted">
                      Montant en FCFA (entier, max 999,999,999,999)
                    </Form.Text>
                  </Col>
                </>
              )}
            </Row>

            {/* ========== TRANSPORT MARITIME - DÉTAILS ========== */}
            {formData.shipment_type === "sea" && (
              <>
                <h4 className="fw-bold mb-3 mt-4" style={{ color: "#C62828" }}>
                  🚢 Détails maritimes
                </h4>
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Label>📦 Numéro de conteneur</Form.Label>
                    <Form.Control
                      type="text"
                      name="container_number"
                      value={formData.container_number}
                      onChange={handleChange}
                      placeholder="Ex: MSCU1234567"
                    />
                    <Form.Text className="text-muted">
                      Optionnel - Pour le tracking maritime
                    </Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Label>📄 Packing List No.</Form.Label>
                    <Form.Control
                      type="text"
                      name="bill_of_lading"
                      value={formData.bill_of_lading}
                      onChange={handleChange}
                      placeholder="Ex: MSC123456789"
                    />
                    <Form.Text className="text-muted">
                      Optionnel - Numéro de connaissement
                    </Form.Text>
                  </Col>
                  <Col md={6}>
                    <Form.Label>⛴️ Navire</Form.Label>
                    <Form.Control
                      type="text"
                      name="current_vessel_name"
                      value={formData.current_vessel_name}
                      onChange={handleChange}
                      placeholder="Ex: MSC ARIES"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>🏢 Compagnie maritime</Form.Label>
                    <Form.Control
                      type="text"
                      name="shipping_line_name"
                      value={formData.shipping_line_name}
                      onChange={handleChange}
                      placeholder="Ex: MSC, CMA CGM, MAERSK"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>📤 Port de chargement</Form.Label>
                    <Form.Control
                      type="text"
                      name="loading_port"
                      value={formData.loading_port}
                      onChange={handleChange}
                      placeholder="Ex: Ningbo, Shanghai, Guangzhou"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>📥 Port de déchargement</Form.Label>
                    <Form.Control
                      type="text"
                      name="discharging_port"
                      value={formData.discharging_port}
                      onChange={handleChange}
                      placeholder="Ex: Douala, Abidjan, Dakar"
                    />
                  </Col>
                </Row>
              </>
            )}

            {/* ========== DATE LIVRAISON ========== */}
            <h4 className="fw-bold mb-3" style={{ color: "#C62828" }}>
              📅 Date livraison prévue
            </h4>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Control
                  type="date"
                  name="expected_delivery_date"
                  value={formData.expected_delivery_date}
                  onChange={handleChange}
                />
                <Form.Text className="text-muted">
                  Laissez vide pour une estimation automatique
                </Form.Text>
              </Col>
            </Row>

            {/* ========== RÉCAPITULATIF ========== */}
            {estimatedCost > 0 && (
              <Alert variant="success" className="d-flex align-items-center">
                <span style={{ fontSize: "24px", marginRight: "12px" }}>
                  💰
                </span>
                <div>
                  <strong>Coût estimé :</strong>{" "}
                  {estimatedCost.toLocaleString()} FCFA
                  <br />
                  <small className="text-muted">
                    {formData.shipment_type === "air"
                      ? `Basé sur ${formData.weight_kg} kg × ${parseFloat(formData.rate_per_kg).toLocaleString()} FCFA/kg`
                      : `Basé sur ${formData.volume_cbm} CBM × ${parseFloat(formData.rate_per_cbm).toLocaleString()} FCFA/CBM`}
                  </small>
                </div>
              </Alert>
            )}

            <Alert variant="info">
              <div className="d-flex align-items-start">
                <span style={{ fontSize: "20px", marginRight: "12px" }}>
                  ℹ️
                </span>
                <div>
                  <strong>Informations :</strong>
                  <ul className="mb-0 mt-1">
                    <li>
                      Un compte client sera automatiquement créé avec le
                      téléphone du destinataire
                    </li>
                    {formData.recipient_email && (
                      <li>
                        Email optionnel utilisé pour les notifications
                        supplémentaires
                      </li>
                    )}
                    <li>
                      Les notifications WhatsApp sont envoyées automatiquement
                    </li>
                  </ul>
                </div>
              </div>
            </Alert>

            {/* ========== BOUTONS ========== */}
            <div className="d-flex gap-3 mt-4">
              <Button
                type="submit"
                disabled={loading}
                style={{ backgroundColor: "#C62828", borderColor: "#C62828" }}
                className="px-4 py-2"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Création en cours...
                  </>
                ) : (
                  "📦 Créer le colis"
                )}
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate("/operator/")}
              >
                Annuler
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ParcelCreate;
